import { useAuth0 } from '@auth0/auth0-react';
import { useCallback, use } from 'react';
import AdminCompanyContext from '../shared/contexts/AdminCompanyContext';
import { SessionExpiryContext, SessionExpiredError } from '../shared/contexts/SessionExpiryContext';
import { ReauthRequiredContext } from '../shared/contexts/ReauthRequiredContext';
import { IdentityRefusedContext } from '../shared/contexts/IdentityRefusedContext';
import { setCorrelationId } from '../lib/logger';

interface FetchOptions extends globalThis.RequestInit {
  idempotencyKey?: string;
}

interface PostOptions {
  idempotencyKey?: string;
  headers?: Record<string, string>;
}

/**
 * Coerce an API error body into a human-readable string. The backend's error
 * shape varies (`{ message }`, `{ error }`, or a Zod `{ error: {...} }` object),
 * and `new Error(someObject)` stringifies to "[object Object]" — so guard for
 * non-string fields and fall back to the status text.
 */
function extractErrorMessage(body: unknown, fallback: string): string {
  if (body && typeof body === 'object') {
    const b = body as Record<string, unknown>;
    if (typeof b.message === 'string' && b.message) return b.message;
    if (typeof b.error === 'string' && b.error) return b.error;
  }
  return fallback;
}

/**
 * A non-OK HTTP response, carrying the two wire facts a caller may need to
 * classify it: the status and the api-gateway error body's `code` (the
 * `{ error, correlationId, code }` shape `middleware/errorHandler.ts` writes
 * for an `HttpError`).
 *
 * Additive on purpose. `.message` is byte-identical to the `Error` this seam
 * threw before, so every existing catch and every message-matching test is
 * untouched; the subclass exists only because a plain `Error` flattened away
 * the status and the code, which made a submit refusal indistinguishable from
 * any other failure (SPEC-SOR-424 — the wizard must tell a quote refusal, which
 * clears its fee record, from a payment refusal, which must not).
 */
export class ApiError extends Error {
  readonly status: number;
  /** The body's error code, when it carried one — the refusal vocabulary rides here. */
  readonly code: string | null;
  readonly body: unknown;

  constructor(message: string, init: { status: number; body: unknown }) {
    super(message);
    this.name = 'ApiError';
    this.status = init.status;
    this.body = init.body;
    const raw = (init.body as { code?: unknown } | null | undefined)?.code;
    this.code = typeof raw === 'string' && raw ? raw : null;
  }
}

async function throwIfNotOk(response: Response): Promise<void> {
  if (response.ok) return;
  const body = await response.json().catch(() => null);
  throw new ApiError(extractErrorMessage(body, response.statusText || `Request failed (${response.status})`), {
    status: response.status,
    body,
  });
}

interface UseApiReturn {
  fetchWithAuth: (url: string, options?: FetchOptions) => Promise<Response>;
  get: <T = unknown>(url: string) => Promise<T>;
  post: <T = unknown>(url: string, data?: unknown, options?: PostOptions) => Promise<T>;
  patch: <T = unknown>(url: string, data?: unknown, options?: PostOptions) => Promise<T>;
  del: <T = unknown>(url: string) => Promise<T>;
  isAuthenticated: boolean;
}

/**
 * Custom hook for making authenticated API calls
 * Automatically includes the Auth0 access token in requests
 * Supports idempotency keys for POST/PATCH requests to prevent duplicates
 */
export function useApi(): UseApiReturn {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  // Platform admins can pick a customer's company in AdminCompanyContext;
  // when set, every API call includes X-Account-Id so the api-gateway
  // resolves the auth context to that customer (auth-context.ts:122-155).
  // The backend ignores this header for non-platform users, so it's safe
  // to attach unconditionally when present.
  const adminCompany = use(AdminCompanyContext);
  const actAsAccountId = adminCompany?.selectedCompanyId ?? null;
  const sessionExpiry = use(SessionExpiryContext);
  const reauth = use(ReauthRequiredContext);
  const identityRefused = use(IdentityRefusedContext);

  /**
   * Make an authenticated fetch request
   */
  const fetchWithAuth = useCallback(
    async (url: string, options: FetchOptions = {}): Promise<Response> => {
      if (!isAuthenticated) {
        throw new Error('User is not authenticated');
      }

      let token: string;
      try {
        token = await getAccessTokenSilently();
      } catch (e) {
        const code = (e as { error?: string }).error;
        if (code === 'login_required' || code === 'missing_refresh_token' || code === 'invalid_grant') {
          sessionExpiry?.triggerExpiry();
          throw new SessionExpiredError();
        }
        throw e;
      }

      // Extract idempotencyKey from options
      const { idempotencyKey, ...fetchOptions } = options;

      // Auto-generate idempotency key for POST/PATCH if not provided
      const method = (fetchOptions.method || 'GET').toUpperCase();
      const shouldAddIdempotencyKey = ['POST', 'PATCH'].includes(method);
      const effectiveIdempotencyKey = idempotencyKey || (shouldAddIdempotencyKey ? crypto.randomUUID() : undefined);

      const isFormData = fetchOptions.body instanceof FormData;

      const headers: Record<string, string> = {
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        ...(actAsAccountId && { 'X-Account-Id': actAsAccountId }),
        ...(fetchOptions.headers as Record<string, string>),
        Authorization: `Bearer ${token}`,
        ...(effectiveIdempotencyKey && { 'Idempotency-Key': effectiveIdempotencyKey }),
      };

      const response = await fetch(url, {
        ...fetchOptions,
        headers,
      });

      // Session-expiry backstop is 401-only. A 403 is an *authorization*
      // outcome (POLICY_DENIED / OWNERSHIP_DENIED / TENANT_SCOPE_DENIED from the
      // api-gateway authz layer), not a stale session — re-authenticating
      // re-mints the same token and hits the same 403, so treating it as expiry
      // produces an infinite login loop (WOL-385). Let 403 fall through to the
      // caller's normal error handling (throwIfNotOk → extractErrorMessage) so
      // it surfaces as an authorization error, not the session modal.
      if (response.status === 401) {
        sessionExpiry?.triggerExpiry();
        throw new SessionExpiredError();
      }

      // Capture correlation ID from response for logging context
      const responseCorrelationId = response.headers.get('X-Correlation-ID');
      if (responseCorrelationId) {
        setCorrelationId(responseCorrelationId);
      }

      // One-login role-claim lag (R15): the first-login invitation-resolution hook
      // sets this header on a 2xx response after writing the freshly-granted
      // platform role into Auth0 app_metadata. The current session's token was
      // minted before the grant, so it does not yet carry the role — surface the
      // re-auth prompt. Single capture point: every API call routes through here,
      // mirroring the 401 session-expiry backstop above. The header literal
      // must match REAUTH_REQUIRED_HEADER in apps/api-gateway (see
      // apps/api-gateway/src/auth/first-login-resolution.ts) — apps can't share
      // constants; HTTP header names are case-insensitive via headers.get.
      if (response.headers.get('X-Reauth-Required') === 'true') {
        reauth?.triggerReauthRequired();
      }

      // Deny-only identity refusal (WOL-393/WOL-471): the auth-context seam
      // sets this header (value = the original connection, e.g. google-oauth2)
      // when it withholds provisioning because the email already has an
      // account under another sign-in method. Same single-capture-point
      // pattern as X-Reauth-Required above; the header literal must match
      // IDENTITY_REFUSED_HEADER in
      // apps/api-gateway/src/auth/identity-unification/deny-policy.ts.
      const refusedConnection = response.headers.get('X-Identity-Refused');
      if (refusedConnection) {
        identityRefused?.triggerIdentityRefused(refusedConnection);
      }

      return response;
    },
    [getAccessTokenSilently, isAuthenticated, actAsAccountId, sessionExpiry, reauth, identityRefused]
  );

  /**
   * Make a GET request
   */
  const get = useCallback(
    async <T = unknown>(url: string): Promise<T> => {
      const response = await fetchWithAuth(url, { method: 'GET' });
      await throwIfNotOk(response);
      return response.json();
    },
    [fetchWithAuth]
  );

  /**
   * Make a POST request
   */
  const post = useCallback(
    async <T = unknown>(url: string, data?: unknown, options: PostOptions = {}): Promise<T> => {
      const response = await fetchWithAuth(url, {
        method: 'POST',
        body: JSON.stringify(data),
        idempotencyKey: options.idempotencyKey,
        ...(options.headers ? { headers: options.headers } : {}),
      });
      await throwIfNotOk(response);
      return response.json();
    },
    [fetchWithAuth]
  );

  /**
   * Make a PATCH request
   */
  const patch = useCallback(
    async <T = unknown>(url: string, data?: unknown, options: PostOptions = {}): Promise<T> => {
      const response = await fetchWithAuth(url, {
        method: 'PATCH',
        body: JSON.stringify(data),
        idempotencyKey: options.idempotencyKey,
        ...(options.headers ? { headers: options.headers } : {}),
      });
      await throwIfNotOk(response);
      return response.json();
    },
    [fetchWithAuth]
  );

  /**
   * Make a DELETE request
   */
  const del = useCallback(
    async <T = unknown>(url: string): Promise<T> => {
      const response = await fetchWithAuth(url, { method: 'DELETE' });
      await throwIfNotOk(response);
      return response.json();
    },
    [fetchWithAuth]
  );

  return {
    fetchWithAuth,
    get,
    post,
    patch,
    del,
    isAuthenticated,
  };
}

/**
 * API base URL - defaults to same origin in production
 * In development, this can be overridden via environment variable
 */
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
